import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { GridConstants } from '@/common/grid/GridConstants';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { ObjUtils } from '@/common/utils/ObjUtils';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * GP-9: This is a step in the Calc Job Dialog Wizard.
 * The user selects a calculation period or, in the case of reproductions, a workbook or an existing calculation
 */
export const PeriodSelectStep = ({ calcChainReq, setCalcChainReq, setNextStep, setStepReady }) => {
  const rsrc = useRsrc();
  const [subtitle, setSubtitle] = useState('Select a policy type to view applicable periods.');

  const [polTypes, setPolTypes] = useState([]);
  const [selectedPolType, setSelectedPolType] = useState(calcChainReq.liabPolType ?? null);
  const [isRepro, setIsRepro] = useState(false);

  const [selectedRowCount, setSelectedRowCount] = useState(0);

  const [defaultReallocMode, setDefaultReallocMode] = useState();

  // Let parent know what the next page is
  useEffect(() => {
    // If null, 'Next' button will be deactivated
    const nextPage =
      selectedRowCount > 0 ?
        isRepro ? 'Products'
        : 'Policy'
      : null;
    setNextStep(nextPage);
  }, [selectedRowCount, isRepro]);

  // Initial data loading
  useEffect(() => {
    const calcEntity = calcChainReq.entity;

    const dataPluginReqParam = {
      calcType: calcEntity,
    };

    rsrc.data('gp', 'CALC_JOB_PERIOD_DATA_PLUGIN', dataPluginReqParam).then((res) => {
      // Get default value for isRepro if no calcs were selected
      if (calcChainReq.calcJobReqs.length === 0) {
        const reproRes = res.defaultRepro ?? 'N';
        const isReproDefault = reproRes.value === 'Y' && calcEntity === 'PRICE_CALC';
        setIsRepro(isReproDefault);
      } else {
        // If rows have been selected, get isRepro through their source (period or workbook)
        const jobReq = calcChainReq.calcJobReqs[0];
        setIsRepro(jobReq.src !== 'PER');
      }

      // Get default value for Realloc Mode if none has been defined yet (and it is defined in the)
      if (!defaultReallocMode && !calcChainReq.reallocMode && res.defaultRealloc) {
        const defaultRealloc = res.defaultRealloc.value;
        setDefaultReallocMode(defaultRealloc);
        setCalcChainReq((prev) => ({ ...prev, reallocMode: defaultRealloc }));
      }

      const enumRes = res.polTypes;
      setPolTypes(enumRes);

      // Default to first of list if no policy type has been set
      if (!selectedPolType) {
        setSelectedPolType(enumRes[0]);
        setCalcChainReq((prev) => ({ ...prev, liabPolType: enumRes[0] }));
      }
    });
  }, []);

  // Field definitions
  const augFieldDefs = (fieldToFieldDef) => {
    if (!isRepro) {
      ObjUtils.clearObj(fieldToFieldDef);

      fieldToFieldDef.periodCode = {
        displayName: 'Period',
        field: 'periodCode',
        filter: 'agTextColumnFilter',
        filterParams: { caseSensitive: true },
        sort: 'desc',
      };

      fieldToFieldDef.checkbox = GridConstants.checkbox;
      return;
    }

    const srcValues = [
      { code: 'Workbook', displayName: 'Workbook' },
      { code: 'Calculation', displayName: 'Calculation' },
    ];

    fieldToFieldDef.checkbox = GridConstants.checkbox;
    const columns = ['checkbox', 'name', 'srcDisplayName', 'polType', 'status', 'periodCode'];

    Object.keys(fieldToFieldDef).forEach((colName) => {
      if (!columns.includes(colName)) {
        delete fieldToFieldDef[colName];
      }
    });

    fieldToFieldDef.periodCode = {
      displayName: 'Period',
      field: 'periodCode',
      filter: 'agTextColumnFilter',
      filterParams: { caseSensitive: true },
      sort: 'desc',
      maxWidth: 200,
    };

    fieldToFieldDef.srcDisplayName = {
      displayName: 'Source',
      field: 'srcDisplayName',
      filter: 'agSetColumnFilter',
      values: srcValues,
    };
  };

  // Query logic
  const entityListRef = useRef();
  const clientSideRowData = useCallback(
    (setDataSource) => {
      if (setStepReady) setStepReady(false);
      let rowsReq = {
        expr: ExprUtils.whereParams('polType', selectedPolType?.code),
      };
      if (isRepro) {
        rowsReq.expr = ExprUtils.and(rowsReq.expr, ExprUtils.whereParams('src', ['WBK', 'CALC']));
      }

      const query = isRepro ? 'CALC_SRCS' : 'PERIOD_SRC';
      rsrc.rows('gp', query, rowsReq).then((rows) => {
        setDataSource(rows);
        if (setStepReady) setStepReady(true);
        console.log(rows);
        const calcReqs = calcChainReq.calcJobReqs;
        if (calcReqs?.length === 0) return;

        const selectedIds = calcReqs.map((req) => (isRepro ? req.rowId : req.periodId));

        const gridRef = entityListRef.current.gridRef.current;
        const gridApiRef = gridRef.gridApiRef.current;

        gridApiRef.forEachNode((node) => {
          const id = isRepro ? node.data?.rowId : node.data?.periodId;
          if (selectedIds.includes(id)) {
            node.setSelected(true);
          }
        });
      });
    },
    [selectedPolType, isRepro],
  );

  const rowSelected = (event) => {
    const selectedRows = event.api.getSelectedRows();

    const updatedCalcJobReqs = selectedRows.map((row) => ({
      ...row,
      period: { code: row.periodCode, id: row.periodId },
      wbkName: row.name,
      name: null,
    }));

    updatedCalcJobReqs.sort((a, b) => a.period.code.localeCompare(b.period.code));

    setCalcChainReq((prev) => ({
      ...prev,
      calcJobReqs: updatedCalcJobReqs,
    }));

    setSelectedRowCount(updatedCalcJobReqs.length);
  };

  const resetSelection = () => {
    setCalcChainReq((prev) => ({ ...prev, calcJobReqs: [] }));
    setSelectedRowCount(0);
  };

  useEffect(() => {
    // Update calc's reallocation mode if none has been set in a future step
    if (!defaultReallocMode) return;

    setCalcChainReq((prev) => ({
      ...prev,
      reallocMode: isRepro ? 'BUNDLE' : defaultReallocMode,
    }));
  }, [isRepro]);

  useEffect(() => {
    if (isRepro) {
      setSubtitle(`Select a Workbook to base your calculation on. ${selectedRowCount} reference workbook(s) selected.`);
    } else {
      setSubtitle(`Select a Period for your calculation. ${selectedRowCount} period(s) selected.`);
    }
  }, [isRepro, selectedRowCount]);

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          my: 1,
        }}
      >
        <Typography variant="h6">{subtitle}</Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'nowrap' }}>
          {/* Reproduction Toggle */}
          <ToggleButtonGroup
            value={isRepro ? 'repro' : 'calc'}
            exclusive
            onChange={(e, value) => {
              resetSelection();
              setIsRepro(value === 'repro');
            }}
            sx={{ height: 40, color: 'primary.main' }}
          >
            <ToggleButton value="calc">Calculation</ToggleButton>
            <ToggleButton value="repro">Reproduction</ToggleButton>
          </ToggleButtonGroup>

          {/* Policy Type Selection */}
          <FormControl sx={{ width: '15rem' }}>
            <InputLabel>
              <Typography variant="body1">Policy Type</Typography>
            </InputLabel>
            <Select
              value={selectedPolType?.code ?? ''}
              onChange={(e) => {
                resetSelection();
                const newCode = e.target.value;
                const newPol = polTypes.find((pt) => pt.code === newCode);
                setSelectedPolType(newPol);
                setCalcChainReq((prev) => ({ ...prev, liabPolType: newPol }));
              }}
              label="Policy Type"
            >
              {polTypes.map((pt) => (
                <MenuItem key={pt.code} value={pt.code}>
                  {pt.code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Grid */}
      <Box sx={{ flex: 1, minHeight: 10, overflow: 'auto' }}>
        <EntityList
          key={`${selectedPolType?.code}-${isRepro}`}
          entity={isRepro ? 'WBK' : 'PERIOD'}
          ref={entityListRef}
          augFieldDefs={augFieldDefs}
          clientSideRowData={selectedPolType ? clientSideRowData : undefined}
          gridOptions={{
            rowSelection: 'multiple',
          }}
          events={{
            onRowSelected: rowSelected,
          }}
        />
      </Box>
    </Box>
  );
};
